Ext.define('SA.view.ViewTypes', {
    singleton: true,

    types: {
        acsTable: {
            icon: 'sa-icon-acs-table',
            newViewScreenDescription: 'The American Community Survey (ACS) is an annual survey from the U.S. Census Bureau that provides thousands of important demographic, social, economic, and housing data variables for research and analysis. Using this ACS report, select any ACS data table and add any locations to quickly view a formatted tabular report of the data.',
            newViewScreenTitle: 'ACS Report',
            viewNameBase: 'ACS Report',

            isAvailableInCountry: countryCode => {
                return SA.Features.hasAcsTable() && SA.Institution.getCountry(countryCode).supportsAcsTables();
            }
        },

        barChart: {
            icon: 'sa-icon-bar-chart',
            newViewScreenDescription: 'Bar charts are a great way to visually compare data values across locations. Add two or more locations to the view and select the desired data variable to create your chart.',
            newViewScreenTitle: 'Bar Chart',
            viewNameBase: 'Bar Chart'
        },

        businessSearchTable: {
            icon: 'sa-icon-business-report',
            newViewScreenDescription: 'Create a data table with detailed information about businesses that you have added to your project. Export the table to Excel or CSV.',
            newViewScreenTitle: 'Business Table',
            viewNameBase: 'Businesses'
        },

        comparison: {
            icon: 'sa-icon-comparison-report',
            newViewScreenTitle: 'Comparison Table',
            viewNameBase: 'Comparison Table',

            newViewScreenDescription: {
                CAN: 'This was called a Standard Report in SimplyMap. Add any types of locations (provinces, census metro areas, census tracts, etc.) and any variables and let us do the rest. It’s simple but very informative.',
                USA: 'This was called a Standard Report in SimplyMap. Add any types of locations (state, county, zip, tract, etc.) and any variables and let us do the rest. It’s simple but very informative.'
            }
        },

        histogram: {
            icon: 'sa-icon-histogram',
            newViewScreenDescription: 'A histogram is a chart that shows the frequency distribution of a data variable for the locations you select (e.g., median income for all counties in Texas). Each bar represents a "bin" of data with the same width as the other bars, and the height of the bar represents the count of the number of locations that have a value that falls within each bin. It’s an extremely useful chart that provides a visual estimate of a variable’s mean, standard deviation, skewness and kurtosis.',
            newViewScreenTitle: 'Histogram',
            viewNameBase: 'Histogram'
        },

        locationQuery: {
            icon: 'sa-icon-location-query',
            newViewScreenDescription: 'Search for locations that match your criteria with this powerful location query view. For example, you can search for all census tracts with a population greater than 5,000 and a median income greater than $150,000. You can add any locations that match your query to your project for use in other views.',
            newViewScreenTitle: 'Location Query',
            viewNameBase: 'Location Query'
        },

        map: {
            icon: 'sa-icon-map',
            newViewScreenDescription: 'Create a thematic map using this View. Simply select a location and a data variable and we will generate a map that you can easily edit to suit your needs. Maps can also be exported as high resolution images.',
            newViewScreenTitle: 'Map',
            viewNameBase: 'Map'
        },

        quickReport: {
            icon: 'sa-icon-quick-report',
            newViewScreenDescription: 'Select this view if you need a comprehensive list of key data variables for any location. Add one or more locations to the report, select the report content (such as a "Demographic Overview") from the dropdown box, and instantly get a nicely formatted, easy-to-read report with detailed information about your selected location(s).',
            newViewScreenTitle: 'Quick Report',
            viewNameBase: 'Quick Report',

            isAvailableInCountry: countryCode => SA.data.quickreport.QuickReportTemplates.areQuickReportsAvailableForCountry(countryCode)
        },

        radiusTable: {
            icon: 'sa-icon-ring-study',
            newViewScreenTitle: 'Ring Study Table',
            viewNameBase: 'Ring Study',

            newViewScreenDescription: {
                CAN: 'Select a central location and get a detailed table containing data for the 1km, 3km and 5km rings around it. Use this when you need to understand the characteristics surrounding a specific location, or to compare locations for site selection.',
                USA: 'Select a central location and get a detailed table containing data for the 1mi, 3mi and 5mi rings around it. Use this when you need to understand the characteristics surrounding a specific location, or to compare locations for site selection.'
            }
        },

        ranking: {
            icon: 'sa-icon-ranking-report',
            newViewScreenTitle: 'Ranking Table',
            viewNameBase: 'Ranking',

            newViewScreenDescription: {
                CAN: 'This was called a Location Analysis Report or a Ranking in SimplyMap. First select one "boundary" location, then select the geographic unit you are interested in, and then add data. For example, you can show data for all Census Tracts in Alberta or DAs in Halifax.',
                USA: 'This was called a Location Analysis Report or a Ranking in SimplyMap. First select one "boundary" location, then select the geographic unit you are interested in, and then add data. For example, you can show data for all ZIPs in Ohio or Tracts in Reno.'
            }
        },

        relatedDataTable: {
            icon: 'sa-icon-related-data-report',
            newViewScreenDescription: 'Add one data variable to this report and we will automatically add all of the related data for you. For example, if you add "% Households w/ Income $50,000 to $74,999" the report will also show all of the other income ranges as well.',
            newViewScreenTitle: 'Related Data Table',
            viewNameBase: 'Related Data Table'
        },

        scarboroughCrosstab: {
            icon: 'sa-icon-crosstab',
            newViewScreenDescription: SA.localize('Scarborough surveys more than 210,000 people 18 years of age and older across the country to capture local consumer trends in large and mid-tier markets. The data includes automotive, health care, media, shopping, internet, banking and more. Surveys are completed market by market during a 12-month period across more than 140 metro survey areas called Designated Market Areas (DMAs). You can create a crosstab table for any DMA and use multiple views to compare differences between consumer behavior and brand preferences between DMAs (e.g., what soda brands are popular among teens in Boston vs. Atlanta?).'),
            newViewScreenTitle: 'Scarborough Crosstab Table',
            viewNameBase: 'Scarborough Crosstab',

            isAvailableInCountry: countryCode => SA.Institution.getCountry(countryCode).supportsScarboroughCrosstabs()
        },

        scatterPlot: {
            icon: 'sa-icon-scatter-plot',
            newViewScreenTitle: 'Scatter Plot',
            viewNameBase: 'Scatter Plot',

            newViewScreenDescription: {
                CAN: 'A scatter plot is a great tool for visualizing the relationship between two data variables. Select a location and a geographic unit (e.g., Census Tracts in Vancouver), then select an x-axis data variable and a y-axis data variable. Each dot represents both the x and y values for a single location. The line of best fit and correlation value indicate the direction and strength of the relationship between the two variables.',
                USA: 'A scatter plot is a great tool for visualizing the relationship between two data variables. Select a location and a geographic unit (e.g., ZIP Codes in Chicago), then select an x-axis data variable and a y-axis data variable. Each dot represents both the x and y values for a single location. The line of best fit and correlation value indicate the direction and strength of the relationship between the two variables.'
            }
        },

        simmonsCrosstab: {
            icon: 'sa-icon-crosstab',
            newViewScreenDescription: SA.localize('Simmons National Consumer Study (NCS) provides detailed data on the lifestyles, attitudes, brand preferences and media use of approximately 25,000 U.S. adults. Data includes over 600 consumer attitudes, opinions, and segmentations, and the measurement of more than 500 product categories and 8,000 brands. Using this data you can create detailed crosstab tables that analyze the relationship between demographic characteristics and any of the consumer behavior data variables.'),
            newViewScreenTitle: 'Simmons Crosstab Table',
            viewNameBase: 'Simmons Crosstab',

            isAvailableInCountry: countryCode => SA.Institution.getCountry(countryCode).supportsSimmonsCrosstabs()
        },

        timeSeries: {
            icon: 'sa-icon-time-series-report',
            newViewScreenDescription: 'Add one data variable to this report and we will automatically add all years of data for that variable. For example, if you add "Median Household Income, 2017" the report will also show data for the years 2000, 2010, 2011, etc.',
            newViewScreenTitle: 'Time Series Table',
            viewNameBase: 'Time Series Table'
        }
    },

    getIcon(type) {
        return this.types[type].icon;
    },

    getNewViewScreenViewListData({countryCode, censusReleaseYear}) {
        var types = [
            'acsTable',
            'map',
            'comparison',
            'ranking',
            'quickReport',
            'radiusTable',
            'businessSearchTable',
            'relatedDataTable',
            'timeSeries',
            'locationQuery',
            'histogram',
            'barChart',
            'scatterPlot',
            'simmonsCrosstab',
            'scarboroughCrosstab'
        ];

        return types
            .filter(type => this.isTypeAvailableInCountry(type, countryCode))
            .map(type => this.getNewViewScreenViewData({type, countryCode, censusReleaseYear}));
    },

    getNewViewScreenViewData({type, countryCode, censusReleaseYear}) {
        var icon = this.getIcon(type),
            title = this.types[type].newViewScreenTitle,
            descriptionOrDescriptions = this.types[type].newViewScreenDescription,
            description = Ext.isString(descriptionOrDescriptions) ? descriptionOrDescriptions : descriptionOrDescriptions[countryCode],
            disabled = false;

        if (this.isSimmonsCrosstab(type)) {
            disabled = !SA.data.Vendors.getSimmonsCrosstabModel(censusReleaseYear);
        }
        else if (this.isScarboroughCrosstab(type)) {
            disabled = !SA.data.Vendors.getScarboroughCrosstabModel().hasCensusReleaseYear(censusReleaseYear);
        }

        return {type, icon, title, description, disabled};
    },

    getViewNameBase(type) {
        return this.types[type].viewNameBase;
    },

    isAcsTable(type) {
        return type === 'acsTable';
    },

    isCrosstab(type) {
        return this.isSimmonsCrosstab(type) || this.isScarboroughCrosstab(type);
    },

    isScarboroughCrosstab(type) {
        return type === 'scarboroughCrosstab';
    },

    isSimmonsCrosstab(type) {
        return type === 'simmonsCrosstab';
    },

    isTypeAvailableInCountry(type, countryCode) {
        var {isAvailableInCountry} = this.types[type];

        return !isAvailableInCountry || isAvailableInCountry(countryCode);
    }
});